import React from 'react';

import { Icon } from '../Icon/Icon';
import { Root, Input, Box, Label } from './Checkbox.styles';

export type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Label copy (i18n key) with optional interpolation. */
  label: string;
  placeholders?: Record<string, string | number>;
  disabled?: boolean;
  /** Identifier surfaced via `event.target.dataset.id` on the native change event (as Toggle). */
  id?: string;
  className?: string;
};

/**
 * Labelled, keyboard-accessible checkbox for settings panels.
 *
 * A visually hidden native `<input type="checkbox">` carries the role, the checked
 * state, keyboard handling (Space toggles) and `disabled`; a custom 18px box paints
 * it and shows the `check` glyph when checked. The whole row is a `<label>`, so
 * clicking the copy toggles the input. React-controlled: `checked` is the source of
 * truth and `onChange` receives the next value.
 */
export const Checkbox = ({
  checked,
  onChange,
  label,
  placeholders,
  disabled = false,
  id,
  className,
}: CheckboxProps): React.ReactElement => (
  <Root className={className} $disabled={disabled}>
    <Input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      data-id={id}
      onChange={(event) => onChange(event.target.checked)}
    />
    <Box aria-hidden="true" $checked={checked} $disabled={disabled}>
      {checked ? <Icon name="check" size={14} /> : null}
    </Box>
    <Label text={label} placeholders={placeholders} tag="span" $disabled={disabled} />
  </Root>
);

export default Checkbox;
